import type { IAgentTool } from '../IAgentTool';
import { resolveNodeByName } from '../utils/resolveNodeByName';
import { UpdateNodeTextCommand } from '../../../commands/UpdateNodeTextCommand';

export const quickEditTool: IAgentTool = {
  name: 'quick_edit',
  description: 'Update a single field on a node by name.',
  parameters: {
    type: 'object',
    properties: {
      node_name: { type: 'string', description: 'Name of the node/person to update' },
      field: { type: 'string', description: 'Field to update (e.g., title, department)' },
      new_value: { type: 'string', description: 'New value for the field' },
    },
    required: ['node_name', 'field', 'new_value'],
  },
  supportedDiagramTypes: [],
  tier: 'structural',
  category: 'edit',

  async execute(params, context) {
    if (context.stores.scenarios.scenarioState === 'EDITING_SCENARIO') {
      return {
        success: false,
        summary: 'Cannot quick-edit while editing a scenario. Exit scenario mode first, or use the scenario tool for batch changes.',
      };
    }

    const nodeName = String(params.node_name ?? '').trim();
    const field = String(params.field ?? '').trim();
    const newValue = String(params.new_value ?? '').trim();

    if (!nodeName || !field) {
      return { success: false, summary: 'Missing node name or field.' };
    }

    const resolved = resolveNodeByName(nodeName, context.diagramData.nodes);
    if (resolved.status === 'not_found') {
      return { success: false, summary: `Could not find "${nodeName}".` };
    }
    if (resolved.status === 'ambiguous') {
      const list = resolved.candidates.map((c) => c.name ?? c.id).join(', ');
      return { success: false, summary: `Multiple matches for "${nodeName}": ${list}. Please be more specific.` };
    }

    const node = resolved.node;
    const currentValue = (node.attrs?.[field] ?? node.props?.[field] ?? (node as any)[field]) as string | number | boolean | null | undefined;
    const oldText = currentValue != null ? String(currentValue) : '';

    const command = new UpdateNodeTextCommand({
      nodeId: node.id,
      field,
      newText: newValue,
      oldText,
    });

    return {
      success: true,
      summary: `Updated ${node.name ?? node.id}'s ${field} to "${newValue}"`,
      createdResourceIds: [command.id],
      execution: { kind: 'command', command },
    };
  },
};
